import { useEffect, useState } from 'react';
import {
  useGetContainerStationsWithManualAmountStockQuery,
  useGetContainerStationsWithManualAmountStockLazyQuery,
} from '../../../graphql/queries/getContainerStationsWithManualAmountStock.generated';
import i18n from 'i18next';
import {
  DateFormatOptionsForExport,
  DateFormatOptionsForFileNames,
  formatNumber,
  getDateFormatOptions,
} from '@/utils/i18n';
import { DEFAULT_CONTAINER_TYPE_ICON } from '@/utils/imageUrl';
import { Button, Table } from 'reactstrap';
import { FaDownload } from 'react-icons/fa';
import { toCsvBlob } from '@/utils/data';
import { download, sanitizeFilename } from '@/utils/download';
import CleanOrDirtyText from '../../../components/CleanOrDirtyText';
import { ContainerStationWithManualAmountStock } from '../../../graphql/generatedTypes';
import ContainerTypeDisplay from '../../../components/ContainerType/ContainerTypeDisplay';
import ContainerStationStatusBadge from '../../../components/ContainerStation/ContainerStationStatusBadge';
import TrackableItemDisplay from '../../../components/TrackableItem/TrackableItemDisplay';

type ItemLocationContainerStationViewProps = {
  itemLocationId?: string | null;
  itemLocationName?: string;
};

const PAGE_SIZE = 20;

export function ItemLocationContainerStationView({
  itemLocationId,
  itemLocationName,
}: ItemLocationContainerStationViewProps) {
  const [page, setPage] = useState(1);
  const [containerStations, setContainerStations] = useState<
    ContainerStationWithManualAmountStock[]
  >([]);
  const [hasMore, setHasMore] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data, loading, error, fetchMore } =
    useGetContainerStationsWithManualAmountStockQuery({
      variables: {
        item_location_id: itemLocationId || '',
        page_size: PAGE_SIZE,
        page: 1,
      },
      skip: !itemLocationId,
    });

  const [getAllContainerStations] =
    useGetContainerStationsWithManualAmountStockLazyQuery();

  useEffect(() => {
    if (data?.getContainerStationsWithManualAmountStock) {
      setContainerStations(data.getContainerStationsWithManualAmountStock);
      setHasMore(
        data.getContainerStationsWithManualAmountStock.length === PAGE_SIZE,
      );
    }
  }, [data]);

  const loadMore = () => {
    fetchMore({
      variables: {
        page: page + 1,
        page_size: PAGE_SIZE,
      },
    }).then((fetchMoreResult) => {
      const newContainerStations =
        fetchMoreResult.data?.getContainerStationsWithManualAmountStock || [];
      setContainerStations([...containerStations, ...newContainerStations]);
      setPage(page + 1);
      setHasMore(newContainerStations.length === PAGE_SIZE);
    });
  };

  async function downloadAllContainerStations() {
    setIsDownloading(true);
    try {
      const { data } = await getAllContainerStations({
        variables: {
          item_location_id: itemLocationId || '',
        },
      });

      if (data?.getContainerStationsWithManualAmountStock) {
        const csvData = data.getContainerStationsWithManualAmountStock.map(
          (item) => {
            const arrivalDateInExportFormat = new Date(
              item.arrival_date,
            ).toLocaleDateString(i18n.language, DateFormatOptionsForExport);

            if (item.container_type_amounts.length === 0) {
              return [
                {
                  container_station_id: item.id,
                  container_type_name: '-',
                  container_type_id: '-',
                  amount_dirty: '-',
                  amount_clean: '-',
                  sum: '-',
                  status: item.sealed ? 'clean' : 'dirty',
                  booked_at: arrivalDateInExportFormat,
                },
              ];
            }

            return item.container_type_amounts.map((containerTypeAmount) => {
              const isGhostContainer = containerTypeAmount.amount === 0;

              return {
                container_station_id: item.id,
                container_type_name: isGhostContainer
                  ? '-'
                  : containerTypeAmount.container_type_name,
                container_type_id: isGhostContainer
                  ? '-'
                  : containerTypeAmount.container_type_id,
                amount_dirty: item.sealed ? 0 : containerTypeAmount.amount,
                amount_clean:
                  item.sealed || !containerTypeAmount.container_type_id
                    ? containerTypeAmount.amount
                    : 0,
                sum: containerTypeAmount.amount,
                status: item.sealed ? 'clean' : 'dirty',
                booked_at: arrivalDateInExportFormat,
              };
            });
          },
        );

        const csvBlob = toCsvBlob(csvData.flat());
        const date = new Date()
          .toLocaleDateString(i18n.language, DateFormatOptionsForFileNames)
          .replaceAll('/', '-');
        const filename = `container_stations_with_manual_stock_in_${sanitizeFilename(
          itemLocationName || '',
        )}_${date}.csv`;

        download(csvBlob, filename);
      }
    } catch (error) {
      console.error('Error downloading container stations:', error);
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <>
      {/* Adding responsive will break the header stickness! */}
      <Table striped hover bordered>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={5}>{i18n.t('Common:loading')}</td>
            </tr>
          )}
          {containerStations.map((station) => {
            if (station.container_type_amounts.length === 0) {
              return (
                <tr key={station.id}>
                  <td>
                    <TrackableItemDisplay
                      trackableItem={station}
                      showTrackableItemId={false}
                      showLink
                      size={'s'}
                    />
                  </td>

                  <td>
                    <ContainerStationStatusBadge status={station.status} />
                  </td>

                  <td>-</td>
                  <td className="numbers-formatted">-</td>
                  <td>-</td>

                  <td>
                    {new Date(station.arrival_date).toLocaleDateString(
                      i18n.language,
                      getDateFormatOptions(true),
                    )}
                  </td>

                  <td>{station.booked_by_user_name}</td>

                  <td>
                    {!station.counted_date && '-'}
                    {station.counted_date &&
                      new Date(station.counted_date).toLocaleDateString(
                        i18n.language,
                        getDateFormatOptions(true),
                      )}
                  </td>

                  <td>
                    {!station.counted_by && '-'}
                    {station.counted_by && station.counted_by}
                  </td>
                </tr>
              );
            } else {
              // if there are more then 0 container_type_amounts
              return (
                <>
                  {station.container_type_amounts.map(
                    (containerTypeAmount, index) => {
                      const isGhostContainer = containerTypeAmount.amount === 0;

                      return (
                        <tr key={station.id}>
                          {index === 0 && (
                            <td rowSpan={station.container_type_amounts.length}>
                              <TrackableItemDisplay
                                trackableItem={station}
                                showTrackableItemId={false}
                                showLink
                                size={'s'}
                              />
                            </td>
                          )}

                          <td>
                            <ContainerStationStatusBadge
                              status={station.status}
                            />
                          </td>

                          <td>
                            {(isGhostContainer ||
                              containerTypeAmount.container_type_name ===
                                null) &&
                              '-'}
                            {!isGhostContainer &&
                              containerTypeAmount.container_type_name !==
                                null && (
                                <>
                                  <ContainerTypeDisplay
                                    containerType={{
                                      id: containerTypeAmount.container_type_id,
                                      imageUrl:
                                        containerTypeAmount.container_type_image ||
                                        DEFAULT_CONTAINER_TYPE_ICON,
                                      name: containerTypeAmount.container_type_name,
                                      hexCode: '',
                                    }}
                                    showContainerId={false}
                                    size="s"
                                    showName
                                  />
                                </>
                              )}
                          </td>

                          <td className="numbers-formatted">
                            {(isGhostContainer ||
                              containerTypeAmount.amount === undefined) &&
                              '-'}
                            {!isGhostContainer &&
                              containerTypeAmount.amount !== undefined &&
                              formatNumber(Number(containerTypeAmount.amount))}
                          </td>

                          <td>
                            {containerTypeAmount.container_type_id &&
                            station.sealed ? (
                              <CleanOrDirtyText isClean />
                            ) : (
                              <CleanOrDirtyText isDirty />
                            )}
                          </td>

                          <td>
                            {new Date(station.arrival_date).toLocaleDateString(
                              i18n.language,
                              getDateFormatOptions(true),
                            )}
                          </td>

                          <td>{station.booked_by_user_name}</td>

                          <td>
                            {!station.counted_date && '-'}
                            {station.counted_date &&
                              new Date(station.counted_date).toLocaleDateString(
                                i18n.language,
                                getDateFormatOptions(true),
                              )}
                          </td>

                          <td>
                            {!station.counted_by && '-'}
                            {station.counted_by && station.counted_by}
                          </td>
                        </tr>
                      );
                    },
                  )}
                </>
              );
            }
          })}
        </tbody>

        <thead>
          {/* This is below tbody on purpose! To have the <th> sticky above the tbody. */}
          <tr>
            <th className="sticky-table-header">
              {i18n.t('ItemLocation:containerStationId')}
            </th>

            <th className="sticky-table-header">
              {i18n.t('Common:boxStatus')}
            </th>

            <th className="sticky-table-header">
              {i18n.t('ItemLocation:containerType')}
            </th>

            <th className="sticky-table-header">
              {i18n.t('ItemLocation:manualAmount')}
            </th>

            <th className="sticky-table-header">
              {i18n.t('Common:containerStatus')}
            </th>

            <th className="sticky-table-header">
              {i18n.t('ItemLocation:lastBooking')}
            </th>

            <th className="sticky-table-header">
              {i18n.t('ItemLocation:bookedByUser')}
            </th>

            <th className="sticky-table-header">
              {i18n.t('ItemLocation:countedAt')}
            </th>

            <th className="sticky-table-header">
              {i18n.t('ItemLocation:countedBy')}
            </th>
          </tr>
        </thead>
      </Table>

      {hasMore && (
        <Button
          className="ms-2 mt-2 mb-2"
          onClick={loadMore}
          disabled={loading}
        >
          {loading ? i18n.t('Common:loading') : i18n.t('Common:loadMore')}
        </Button>
      )}

      <Button
        className="float-end ms-2 mt-2 mb-2"
        onClick={downloadAllContainerStations}
        disabled={isDownloading || !containerStations.length}
      >
        {isDownloading ? (
          i18n.t('Common:loading')
        ) : (
          <>
            <FaDownload />
            {' ' + i18n.t('Common:download')}
          </>
        )}
      </Button>
    </>
  );
}
